import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Search,
  ClipboardList,
  User,
  BarChart3,
  FolderKanban,
  Loader2,
  CornerDownLeft,
  Clock,
} from "lucide-react"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"

interface SearchResult {
  type: "assessment" | "candidate" | "report" | "project"
  id: string
  title: string
  subtitle: string
  link: string
  badge?: string | null
}

const TYPE_META: Record<
  SearchResult["type"],
  { icon: typeof Search; label: string; section: string }
> = {
  assessment: { icon: ClipboardList, label: "Assessment", section: "Assessments" },
  candidate: { icon: User, label: "Candidate", section: "Candidates" },
  report: { icon: BarChart3, label: "Report", section: "Reports" },
  project: { icon: FolderKanban, label: "Project", section: "Projects" },
}

const RECENTS_KEY = "yoma.search.recents"

function readRecents(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]")
    return Array.isArray(v) ? v.slice(0, 5) : []
  } catch {
    return []
  }
}
function pushRecent(q: string) {
  if (!q.trim()) return
  try {
    const next = [q, ...readRecents().filter((x) => x !== q)].slice(0, 5)
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next))
  } catch {
    /* ignore */
  }
}

export function GlobalSearch({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const navigate = useNavigate()
  const [q, setQ] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [active, setActive] = useState(0)
  const [recents, setRecents] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<number | null>(null)

  // Reset + focus on open.
  useEffect(() => {
    if (open) {
      setQ("")
      setResults([])
      setActive(0)
      setRecents(readRecents())
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Debounced search (300ms).
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    if (q.trim().length < 2) {
      setResults([])
      setLoading(false)
      return
    }
    setLoading(true)
    debounceRef.current = window.setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: q.trim() } })
        setResults((data.data as SearchResult[]) ?? [])
        setActive(0)
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 300)
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
  }, [q])

  const choose = useCallback(
    (r: SearchResult) => {
      pushRecent(q.trim())
      onOpenChange(false)
      navigate(r.link)
    },
    [q, navigate, onOpenChange],
  )

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === "Enter" && results[active]) {
      e.preventDefault()
      choose(results[active])
    }
  }

  // Group results by section, preserving the backend order.
  const sections: { title: string; items: SearchResult[] }[] = []
  for (const r of results) {
    const sec = TYPE_META[r.type].section
    let bucket = sections.find((s) => s.title === sec)
    if (!bucket) {
      bucket = { title: sec, items: [] }
      sections.push(bucket)
    }
    bucket.items.push(r)
  }
  let flatIdx = -1

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // The shared DialogContent renders a built-in absolute close (X)
        // button at top-right. In this command-palette layout it overlaps
        // the Esc hint, so we hide it *only here* (Esc + backdrop click
        // still close the modal). The shared Dialog is untouched.
        className="top-[15%] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-xl [&>button.absolute]:hidden"
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-2.5 border-b border-[var(--color-border)] px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-[color:var(--color-text-muted)]" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search assessments, candidates, reports…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-[color:var(--color-text-muted)]"
          />
          {loading ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[color:var(--color-text-muted)]" />
          ) : (
            <kbd className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-500">
              Esc
            </kbd>
          )}
        </div>

        <div className="max-h-[55vh] overflow-y-auto py-2">
          {q.trim().length < 2 ? (
            recents.length > 0 ? (
              <div className="px-2">
                <p className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                  Recent searches
                </p>
                {recents.map((r) => (
                  <button
                    key={r}
                    onClick={() => setQ(r)}
                    className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm hover:bg-[var(--color-bg-subtle)]"
                  >
                    <Clock className="h-4 w-4 text-[color:var(--color-text-muted)]" />
                    {r}
                  </button>
                ))}
              </div>
            ) : (
              <p className="px-5 py-10 text-center text-sm text-[color:var(--color-text-muted)]">
                Type at least 2 characters to search.
              </p>
            )
          ) : results.length === 0 && !loading ? (
            <p className="px-5 py-10 text-center text-sm text-[color:var(--color-text-muted)]">
              No results for “{q.trim()}”
            </p>
          ) : (
            sections.map((sec) => (
              <div key={sec.title} className="px-2">
                <p className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                  {sec.title}
                </p>
                {sec.items.map((r) => {
                  flatIdx += 1
                  const idx = flatIdx
                  const Icon = TYPE_META[r.type].icon
                  return (
                    <button
                      key={`${r.type}-${r.id}`}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => choose(r)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-md px-3 py-2 text-left",
                        idx === active
                          ? "bg-[var(--color-accent-light)]"
                          : "hover:bg-[var(--color-bg-subtle)]",
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-[color:var(--color-accent-hover)]" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {r.title}
                        </div>
                        <div className="truncate text-xs text-[color:var(--color-text-muted)]">
                          {r.subtitle}
                        </div>
                      </div>
                      {r.badge ? (
                        <span className="rounded-full bg-[var(--color-bg-muted)] px-2 py-0.5 text-[10px] font-medium text-[color:var(--color-text-secondary)]">
                          {r.badge}
                        </span>
                      ) : null}
                      <span className="text-[10px] text-[color:var(--color-text-muted)]">
                        {TYPE_META[r.type].label}
                      </span>
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-4 border-t border-[var(--color-border)] px-4 py-2 text-[11px] text-[color:var(--color-text-muted)]">
          <span className="inline-flex items-center gap-1">
            <kbd className="rounded border border-[var(--color-border)] px-1">↑</kbd>
            <kbd className="rounded border border-[var(--color-border)] px-1">↓</kbd>
            Navigate
          </span>
          <span className="inline-flex items-center gap-1">
            <CornerDownLeft className="h-3 w-3" /> Open
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className="rounded border border-[var(--color-border)] px-1">Esc</kbd>
            Close
          </span>
        </div>
      </DialogContent>
    </Dialog>
  )
}
